import { useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";

/**
 * Null-rendering bridge component that restores booking state from draft_booking_data
 * returned by the campground API when a ?draftbooking=TOKEN URL param is present.
 *
 * Must be rendered inside both CampgroundProvider and BookingProvider.
 */
export default function DraftBookingApplier() {
  const { data: campgroundData } = useCampground();
  const { actions } = useBookingContext();
  const navigate = useNavigate();
  const { language, campground } = useParams<{ language: string; campground: string }>();
  const [searchParams] = useSearchParams();
  const applied = useRef(false);

  useEffect(() => {
    const draft = campgroundData.draft_booking_data;
    if (!draft || applied.current) return;
    applied.current = true;

    // Resolve campsite entry — pitches use `campsites`, accommodations use `accommodation_types`
    const campsiteEntry = draft.campsites?.[0] ?? draft.accommodation_types?.[0];
    const persons =
      draft.campsites?.[0]?.persons ??
      draft.accommodation_types?.[0]?.accommodation_type_persons ??
      [];

    if (campsiteEntry) {
      const campsite = campgroundData.campground.campsites.find((c) => c.id === campsiteEntry.id);
      if (!campsite) {
        // Campsite was removed since the draft was saved — restart from scratch
        navigate(`/${language}/${campground}/search/dates`, { replace: true });
        return;
      }
      actions.setSelectedCampsiteType(campsite.campsite_type);
      actions.setSelectedCampsite(campsite);
      actions.setSelectedPersons(
        persons.reduce<Record<number, number>>((acc, p) => {
          acc[p.id] = p.amount;
          return acc;
        }, {}),
      );
    }

    actions.setSelectedExtras(draft.extras);

    actions.setDateFrom(new Date(draft.date_from));
    actions.setDateUntil(new Date(draft.date_until));

    actions.setBilling("name", draft.first_name);
    actions.setBilling("lastname", draft.last_name);
    actions.setBilling("mail", draft.email);
    actions.setBilling("phone", draft.phone);
    actions.setBilling("street", draft.street1);
    actions.setBilling("street_additional", draft.street2 ?? "");
    actions.setBilling("zip", draft.zip_code);
    actions.setBilling("city", draft.city);
    actions.setBilling("country", { code: draft.country });
    actions.setBilling("message", draft.message ?? "");

    actions.setCar("license_plate", draft.license_plate ?? "");
    actions.setCar("length", draft.vehicle_length);
    actions.setCar("width", draft.vehicle_width);

    draft.guests?.forEach((guest, index) => {
      actions.setTraveller(index, "first_name", guest.first_name);
      actions.setTraveller(index, "last_name", guest.last_name);
      actions.setTraveller(index, "birthdate", new Date(guest.birthdate));
    });

    actions.setDetailsValid(true);

    const token = draft.draft_booking_id ?? draft.id ?? null;
    if (token) {
      actions.setDraftBookingToken(token);
    }

    // Navigate to results, dropping the ?draftbooking param in the same call
    // so a page refresh doesn't re-apply the draft over any changes the user made.
    const params = new URLSearchParams(searchParams);
    params.delete("draftbooking");
    const search = params.toString();
    navigate(`/${language}/${campground}/search/results${search ? `?${search}` : ""}`, {
      replace: true,
    });
  }, [campgroundData, actions, navigate, language, campground, searchParams]);

  return null;
}
